/* TaskTemplates — /admin/task-templates
   Manage task templates and their event-based triggers.
   Templates auto-create tasks on case_created or stage_changed events. */

import { useState } from 'react';
import css from '../AdminPage.module.css';
import {
  useTaskTemplates,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
  useTaskTemplateTriggers,
  useAddTrigger,
  useRemoveTrigger,
  type TaskTemplate,
  type AddTriggerInput,
} from '../../../hooks/useTaskTemplates';
import { useActivityCodes } from '../../../hooks/useActivityCodes';
import { useAdminUsers, usePipelines, usePipelineStages } from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';

const CATEGORIES = ['admin', 'client', 'court', 'defendant', 'misc'];

// ─── Trigger row ──────────────────────────────────────────────────────────────

function TriggerRow({
  templateId,
  pipelines,
}: {
  templateId: number;
  pipelines: { id: number; name: string }[];
}) {
  const { data: triggers = [], isLoading } = useTaskTemplateTriggers(templateId);
  const addTrigger = useAddTrigger(templateId);
  const removeTrigger = useRemoveTrigger(templateId);
  const { showToast } = useToast();

  const [event, setEvent] = useState<'case_created' | 'stage_changed'>('case_created');
  const [pipelineId, setPipelineId] = useState<number | null>(null);
  const [stageId, setStageId] = useState<number | null>(null);

  const { data: stages = [] } = usePipelineStages(pipelineId);

  const handleAdd = async () => {
    const input: AddTriggerInput = {
      trigger_event: event,
      case_type_id: pipelineId,
      trigger_stage_id: event === 'stage_changed' ? stageId : null,
    };
    try {
      await addTrigger.mutateAsync(input);
      showToast('Trigger added', 'success');
      setEvent('case_created');
      setPipelineId(null);
      setStageId(null);
    } catch {
      showToast('Failed to add trigger', 'error');
    }
  };

  return (
    <div style={{ marginTop: 8 }}>
      {isLoading ? (
        <p style={{ fontSize: '0.8em' }}>Loading…</p>
      ) : triggers.length === 0 ? (
        <p style={{ fontSize: '0.8em', opacity: 0.6 }}>No triggers yet</p>
      ) : (
        <table className={css.table} style={{ fontSize: '0.8em' }}>
          <thead>
            <tr>
              <th>Event</th>
              <th>Pipeline</th>
              <th>Stage</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {triggers.map((t) => (
              <tr key={t.id}>
                <td>
                  <span className={css.badge}>{t.trigger_event}</span>
                </td>
                <td>{t.case_type_id ?? '—'}</td>
                <td>{t.stage_name ?? (t.trigger_stage_id ? String(t.trigger_stage_id) : '—')}</td>
                <td>
                  <button
                    className={`${css.btnSmall} ${css.btnDanger}`}
                    onClick={async () => {
                      await removeTrigger.mutateAsync(t.id);
                      showToast('Trigger removed', 'success');
                    }}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add trigger form */}
      <div
        style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}
      >
        <select
          value={event}
          onChange={(e) => setEvent(e.target.value as 'case_created' | 'stage_changed')}
          style={{ fontSize: '0.8em' }}
        >
          <option value="case_created">case_created</option>
          <option value="stage_changed">stage_changed</option>
        </select>
        <select
          value={pipelineId ?? ''}
          onChange={(e) => {
            setPipelineId(e.target.value ? Number(e.target.value) : null);
            setStageId(null);
          }}
          style={{ fontSize: '0.8em' }}
        >
          <option value="">All pipelines</option>
          {pipelines.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        {event === 'stage_changed' && (
          <select
            value={stageId ?? ''}
            onChange={(e) => setStageId(e.target.value ? Number(e.target.value) : null)}
            style={{ fontSize: '0.8em' }}
          >
            <option value="">Any stage</option>
            {stages.map((s: { stage_id: number; stage_name: string }) => (
              <option key={s.stage_id} value={s.stage_id}>
                {s.stage_name}
              </option>
            ))}
          </select>
        )}
        <button
          className={`${css.btnSmall} ${css.btnPrimary}`}
          onClick={handleAdd}
          disabled={addTrigger.isPending}
        >
          + Add
        </button>
      </div>
    </div>
  );
}

// ─── Template form modal ──────────────────────────────────────────────────────

interface FormState {
  name: string;
  description: string;
  category: string;
  default_assignee_strategy: string;
  default_assignee_user_id: string;
  deadline_offset_days: string;
  activity_code_id: string;
  billable_default: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  category: 'admin',
  default_assignee_strategy: 'case_owner',
  default_assignee_user_id: '',
  deadline_offset_days: '7',
  activity_code_id: '',
  billable_default: true,
};

function templateToForm(t: TaskTemplate): FormState {
  return {
    name: t.name,
    description: t.description,
    category: t.category,
    default_assignee_strategy: t.default_assignee_strategy,
    default_assignee_user_id: t.default_assignee_user_id ?? '',
    deadline_offset_days: String(t.deadline_offset_days),
    activity_code_id: t.activity_code_id ? String(t.activity_code_id) : '',
    billable_default: t.billable_default,
  };
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function TaskTemplates() {
  const { data: templates = [], isLoading } = useTaskTemplates();
  const { data: activityCodes = [] } = useActivityCodes();
  const { data: users = [] } = useAdminUsers();
  const { data: pipelines = [] } = usePipelines();
  const { showToast } = useToast();

  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  const deleteTemplate = useDeleteTemplate();

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (t: TaskTemplate) => {
    setEditingId(t.id);
    setForm(templateToForm(t));
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.description.trim()) {
      showToast('Name and description are required', 'error');
      return;
    }
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      category: form.category,
      default_assignee_strategy: form.default_assignee_strategy,
      default_assignee_user_id: form.default_assignee_user_id || null,
      deadline_offset_days: Number(form.deadline_offset_days) || 7,
      activity_code_id: form.activity_code_id ? Number(form.activity_code_id) : null,
      billable_default: form.billable_default,
    };
    try {
      if (editingId) {
        await updateTemplate.mutateAsync({ id: editingId, patch: payload });
        showToast('Template updated', 'success');
      } else {
        await createTemplate.mutateAsync(payload);
        showToast('Template created', 'success');
      }
      setShowModal(false);
    } catch {
      showToast('Failed to save template', 'error');
    }
  };

  const handleDeactivate = async (id: number) => {
    try {
      await deleteTemplate.mutateAsync(id);
      showToast('Template deactivated', 'success');
    } catch {
      showToast('Failed to deactivate template', 'error');
    }
  };

  const set =
    (key: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <div className={css.section}>
      <div className={css.sectionHeader}>
        <div>
          <h2 className={css.sectionTitle}>Task Templates</h2>
          <p className={css.sectionSubtitle}>
            Define reusable task definitions that auto-generate when case events fire.
          </p>
        </div>
        <button className={css.btnPrimary} onClick={openCreate}>
          + New Template
        </button>
      </div>

      {isLoading ? (
        <p>Loading…</p>
      ) : templates.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No templates yet. Create one to get started.</p>
      ) : (
        <table className={css.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Assignee Strategy</th>
              <th>Deadline Offset</th>
              <th>Activity Code</th>
              <th>Billable</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <>
                <tr
                  key={t.id}
                  onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <strong>{t.name}</strong>
                    <div style={{ fontSize: '0.8em', opacity: 0.7 }}>{t.description}</div>
                  </td>
                  <td>
                    <span className={css.badge}>{t.category}</span>
                  </td>
                  <td>{t.default_assignee_strategy}</td>
                  <td>{t.deadline_offset_days}d</td>
                  <td>{t.activity_code ? `${t.activity_code} — ${t.activity_code_name}` : '—'}</td>
                  <td>{t.billable_default ? '✓' : '—'}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      className={`${css.btnSmall} ${css.btnSecondary}`}
                      onClick={() => openEdit(t)}
                    >
                      Edit
                    </button>{' '}
                    <button
                      className={`${css.btnSmall} ${css.btnDanger}`}
                      onClick={() => handleDeactivate(t.id)}
                    >
                      Deactivate
                    </button>
                  </td>
                </tr>
                {expandedId === t.id && (
                  <tr key={`${t.id}-triggers`}>
                    <td
                      colSpan={7}
                      style={{ background: 'var(--surface-raised, #f8f8f8)', padding: '12px 16px' }}
                    >
                      <strong style={{ fontSize: '0.85em' }}>Triggers</strong>
                      <TriggerRow
                        templateId={t.id}
                        pipelines={pipelines as { id: number; name: string }[]}
                      />
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      )}

      {/* ── Modal ── */}
      {showModal && (
        <div className={css.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{editingId ? 'Edit Template' : 'New Template'}</h3>

            <div className={css.formGroup}>
              <label>Name</label>
              <input type="text" value={form.name} onChange={set('name')} />
            </div>
            <div className={css.formGroup}>
              <label>Description (task text)</label>
              <textarea value={form.description} onChange={set('description')} rows={3} />
            </div>
            <div className={css.formGroup}>
              <label>Category</label>
              <select value={form.category} onChange={set('category')}>
                {CATEGORIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className={css.formGroup}>
              <label>Assignee Strategy</label>
              <select
                value={form.default_assignee_strategy}
                onChange={set('default_assignee_strategy')}
              >
                <option value="case_owner">Case owner (solicitor)</option>
                <option value="specific_user">Specific user</option>
              </select>
            </div>
            {form.default_assignee_strategy === 'specific_user' && (
              <div className={css.formGroup}>
                <label>Specific User</label>
                <select
                  value={form.default_assignee_user_id}
                  onChange={set('default_assignee_user_id')}
                >
                  <option value="">— Select —</option>
                  {users.map((u) => (
                    <option key={u.username} value={u.username}>
                      {u.full_name || u.username}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className={css.formGroup}>
              <label>Deadline Offset (days)</label>
              <input
                type="number"
                min={1}
                value={form.deadline_offset_days}
                onChange={set('deadline_offset_days')}
              />
            </div>
            <div className={css.formGroup}>
              <label>Activity Code (optional)</label>
              <select value={form.activity_code_id} onChange={set('activity_code_id')}>
                <option value="">— None —</option>
                {activityCodes.map((ac) => (
                  <option key={ac.id} value={ac.id}>
                    {ac.code} — {ac.name}
                  </option>
                ))}
              </select>
            </div>
            <div className={css.formGroup}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.billable_default}
                  onChange={(e) => setForm((p) => ({ ...p, billable_default: e.target.checked }))}
                />
                Billable by default
              </label>
            </div>

            <div className={css.modalActions}>
              <button className={css.btnSecondary} onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button
                className={css.btnPrimary}
                onClick={handleSave}
                disabled={createTemplate.isPending || updateTemplate.isPending}
              >
                {editingId ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
